import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import DataGrid, {
  Column,
  FilterRow,
  HeaderFilter,
  Item,
  Paging,
  SearchPanel,
  Toolbar,
} from "devextreme-react/data-grid";
import { Popup } from "devextreme-react/popup";
import Form, { Label, RangeRule, RequiredRule, SimpleItem } from "devextreme-react/form";
import type { default as FormInstance } from "devextreme/ui/form";
import { Button } from "devextreme-react/button";
import NumberBox from "devextreme-react/number-box";
import { FiEdit, FiTrash2, FiCheckCircle } from "react-icons/fi";
import notify from "devextreme/ui/notify";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver";
import { exportDataGrid } from "devextreme/excel_exporter";
import axios, { AxiosError } from "axios";

import GridContainer from "../Productos/GridContainer";
import * as S from "./Manifold.styles";
import { apiService } from "../../../services/apiService";
import { ApiError } from "../../../services/apiError";
import { productosService } from "../../../services/productosService";
import { Sucursal } from "../../../dtos/Sucursal";
import {
  ManifoldGridRow,
  RegistroManifold,
  RegistroManifoldDto,
  RegistroManifoldProducto,
} from "../../../dtos/RegistroManifold";
import {
  getSessionSucursalId,
  isManifoldCatalogReadOnly,
} from "../../../helpers/session";

const handleApiError = (err: unknown): ApiError => {
  if (axios.isAxiosError(err)) {
    const error = err as AxiosError<ApiError>;
    return {
      message:
        error.response?.data?.message || "Ocurrió un error en el servidor.",
      status: error.response?.status,
      details: error.response?.data?.details || null,
    };
  }
  return {
    message: "Ocurrió un error inesperado. Revisa tu conexión de red.",
  };
};

const isActivoEstatus = (estatus?: string | null) =>
  !estatus || estatus.trim().toLowerCase() === "activo";

const pick = <T,>(obj: Record<string, unknown>, keys: string[]): T | undefined => {
  for (const key of keys) {
    if (obj[key] !== undefined && obj[key] !== null) {
      return obj[key] as T;
    }
  }
  return undefined;
};

const normalizeProductoAsociado = (
  raw: unknown,
  fallbackCodigo = 0,
): RegistroManifoldProducto => {
  const item = (raw ?? {}) as Record<string, unknown>;
  const productoRaw = (pick<Record<string, unknown>>(item, ["producto", "Producto"]) ??
    {}) as Record<string, unknown>;
  const codigo = Number(
    pick(item, ["codigo_Producto", "Codigo_Producto", "codigoProducto"]) ??
      pick(productoRaw, ["codigo", "Codigo"]) ??
      fallbackCodigo,
  );

  return {
    id: Number(pick(item, ["id", "Id"]) ?? 0) || undefined,
    id_Registro: Number(pick(item, ["id_Registro", "Id_Registro"]) ?? 0) || undefined,
    codigo_Producto: codigo,
    producto: {
      codigo: Number(pick(productoRaw, ["codigo", "Codigo"]) ?? codigo),
      descripcion: String(pick(productoRaw, ["descripcion", "Descripcion"]) ?? ""),
      caducidad: null,
      capacidad: null,
      um: null,
      claveRegistro: null,
    },
  };
};

const normalizeRegistro = (raw: unknown): RegistroManifold => {
  const item = (raw ?? {}) as Record<string, unknown>;
  const sucursalRaw = (pick<Record<string, unknown>>(item, ["sucursal", "Sucursal"]) ??
    {}) as Record<string, unknown>;
  const productoRaw = (pick<Record<string, unknown>>(item, ["producto", "Producto"]) ??
    {}) as Record<string, unknown>;

  const idSucursal = Number(
    pick(item, ["id_Sucursal", "iD_Sucursal", "ID_Sucursal", "idSucursal"]) ?? 0,
  );
  const codigoProducto = Number(
    pick(item, ["codigo_Producto", "Codigo_Producto", "codigoProducto"]) ?? 0,
  );
  const estatus = String(pick(item, ["estatus", "Estatus"]) ?? "Activo");
  const asociadosRaw =
    pick<unknown[]>(item, ["productosManifold", "ProductosManifold", "productos"]) ??
    [];
  const productos = (Array.isArray(asociadosRaw) ? asociadosRaw : [])
    .map((p) => normalizeProductoAsociado(p))
    .filter((p) => p.codigo_Producto > 0);

  if (productos.length === 0 && codigoProducto > 0) {
    productos.push(
      normalizeProductoAsociado(
        { codigo_Producto: codigoProducto, producto: productoRaw },
        codigoProducto,
      ),
    );
  }

  return {
    id_Registro: Number(
      pick(item, ["id_Registro", "Id_Registro", "idRegistro"]) ?? 0,
    ),
    nombre: String(pick(item, ["nombre", "Nombre"]) ?? ""),
    id_Sucursal: idSucursal,
    codigo_Producto: productos[0]?.codigo_Producto ?? codigoProducto,
    productos,
    capacidad: Number(pick(item, ["capacidad", "Capacidad"]) ?? 0),
    estatus,
    fechaAlta: pick<string>(item, ["fechaAlta", "FechaAlta"]),
    fechaEdicion: pick<string | null>(item, ["fechaEdicion", "FechaEdicion"]),
    sucursal: {
      id: String(pick(sucursalRaw, ["id", "ID"]) ?? idSucursal),
      numSucursal: Number(
        pick(sucursalRaw, ["numSucursal", "NumSucursal"]) ?? 0,
      ),
      nombreSuc: String(
        pick(sucursalRaw, ["nombreSuc", "NombreSuc"]) ?? "",
      ),
      um: "",
      capacidad: 0,
      estatusActivo: pick<boolean>(sucursalRaw, [
        "estatusActivo",
        "EstatusActivo",
      ]),
    },
    producto: {
      codigo: Number(
        pick(productoRaw, ["codigo", "Codigo"]) ??
          productos[0]?.codigo_Producto ??
          codigoProducto,
      ),
      descripcion: String(
        pick(productoRaw, ["descripcion", "Descripcion"]) ??
          productos[0]?.producto?.descripcion ??
          "",
      ),
      caducidad: null,
      capacidad: null,
      um: null,
      claveRegistro: null,
    },
  };
};

const toGridRow = (registro: RegistroManifold): ManifoldGridRow => {
  const activo = isActivoEstatus(registro.estatus);
  const productos =
    registro.productos.length > 0
      ? registro.productos
      : registro.codigo_Producto
        ? [
            {
              codigo_Producto: registro.codigo_Producto,
              producto: registro.producto,
            },
          ]
        : [];
  const codigos = productos
    .map((p) => p.codigo_Producto)
    .filter((c) => c > 0)
    .join(", ");
  const detalles = productos
    .map((p) => p.producto?.descripcion?.trim())
    .filter((d): d is string => Boolean(d))
    .join("; ");

  return {
    id_Registro: registro.id_Registro,
    nombre: registro.nombre,
    id_Sucursal: registro.id_Sucursal,
    numCodSucursal: registro.sucursal?.numSucursal || "—",
    nombreSucursal: registro.sucursal?.nombreSuc || "—",
    codigoProducto: codigos || "—",
    detalleProducto: detalles || "—",
    capacidad: registro.capacidad > 0 ? registro.capacidad : "—",
    estatus: activo ? "Activo" : "Inactivo",
    activo,
  };
};

interface SucursalOption extends Sucursal {
  idNum: number;
}

interface ProductoFormRow {
  id: string;
  codigo: number | null;
  descripcion: string;
}

interface FormState {
  id_Registro?: number;
  nombre: string;
  id_Sucursal: number | null;
  capacidad: number | null;
}

const newProductoRowId = () =>
  `prod-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const emptyProductoRow = (): ProductoFormRow => ({
  id: newProductoRowId(),
  codigo: null,
  descripcion: "",
});

const ManifoldIndex: React.FC = () => {
  const navigate = useNavigate();
  const dataGridRef = useRef<any>(null);
  const formRef = useRef<FormInstance | null>(null);
  const soloLectura = isManifoldCatalogReadOnly();
  const sucursalSesion = getSessionSucursalId();

  const [dataSource, setDataSource] = useState<ManifoldGridRow[]>([]);
  const [registros, setRegistros] = useState<RegistroManifold[]>([]);
  const [sucursales, setSucursales] = useState<SucursalOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFormPopupVisible, setIsFormPopupVisible] = useState(false);
  const [editingItem, setEditingItem] = useState<FormState | null>(null);
  const [productoRows, setProductoRows] = useState<ProductoFormRow[]>([
    emptyProductoRow(),
  ]);
  const [isStatusPopupVisible, setIsStatusPopupVisible] = useState(false);
  const [itemToToggle, setItemToToggle] = useState<ManifoldGridRow | null>(null);
  const productoLookupTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>(
    {},
  );
  const productoLookupSeq = useRef<Record<string, number>>({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [registrosRes, sucursalesRes] = await Promise.all([
        apiService.get<unknown[]>("/api/Catalogos/Registro_Manifold"),
        apiService.get<Sucursal[]>("/api/Catalogos/Sucursales"),
      ]);

      const normalized = (registrosRes ?? [])
        .map(normalizeRegistro)
        .filter((r) =>
          soloLectura
            ? sucursalSesion != null && r.id_Sucursal === sucursalSesion
            : true,
        );
      setRegistros(normalized);
      setDataSource(normalized.map(toGridRow));
      setSucursales(
        (sucursalesRes ?? []).map((s) => ({
          ...s,
          idNum: Number(s.id),
        })),
      );
    } catch (err) {
      notify(handleApiError(err).message, "error", 3000);
    } finally {
      setLoading(false);
    }
  }, [soloLectura, sucursalSesion]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resolveProducto = async (rowId: string, codigo: number | null | undefined) => {
    const seq = (productoLookupSeq.current[rowId] ?? 0) + 1;
    productoLookupSeq.current[rowId] = seq;

    if (!codigo || codigo <= 0) {
      setProductoRows((rows) =>
        rows.map((row) => (row.id === rowId ? { ...row, descripcion: "" } : row)),
      );
      return;
    }

    setProductoRows((rows) =>
      rows.map((row) =>
        row.id === rowId ? { ...row, descripcion: "Buscando..." } : row,
      ),
    );

    try {
      const producto = await productosService.getByCodigo(codigo);
      if (productoLookupSeq.current[rowId] !== seq) return;
      setProductoRows((rows) =>
        rows.map((row) =>
          row.id === rowId
            ? { ...row, descripcion: producto?.descripcion || "" }
            : row,
        ),
      );
    } catch {
      if (productoLookupSeq.current[rowId] !== seq) return;
      setProductoRows((rows) =>
        rows.map((row) =>
          row.id === rowId
            ? { ...row, descripcion: "Producto no encontrado" }
            : row,
        ),
      );
    }
  };

  const handleProductoCodigoChange = (rowId: string, value: number | null) => {
    setProductoRows((rows) =>
      rows.map((row) =>
        row.id === rowId ? { ...row, codigo: value, descripcion: "" } : row,
      ),
    );

    const pending = productoLookupTimers.current[rowId];
    if (pending) {
      clearTimeout(pending);
    }

    if (!value || value <= 0) {
      void resolveProducto(rowId, value);
      return;
    }

    productoLookupTimers.current[rowId] = setTimeout(() => {
      void resolveProducto(rowId, value);
    }, 400);
  };

  const handleAddProducto = () => {
    setProductoRows((rows) => [...rows, emptyProductoRow()]);
  };

  const handleRemoveProducto = (rowId: string) => {
    setProductoRows((rows) =>
      rows.length <= 1 ? rows : rows.filter((row) => row.id !== rowId),
    );
  };

  const handleExportExcel = async () => {
    const gridInstance = dataGridRef.current?.instance();
    if (!gridInstance) {
      notify("No se pudo obtener la tabla para exportar.", "error", 3000);
      return;
    }

    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet("Manifold");

    await exportDataGrid({
      component: gridInstance,
      worksheet,
      autoFilterEnabled: true,
      customizeCell: ({ gridCell, excelCell }) => {
        if (gridCell?.rowType === "header") {
          excelCell.font = { bold: true };
        }
      },
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], { type: "application/octet-stream" }),
      "ConfiguracionManifold.xlsx",
    );
  };

  const handleAdd = () => {
    if (soloLectura) return;
    setEditingItem({
      nombre: "",
      id_Sucursal: null,
      capacidad: null,
    });
    setProductoRows([emptyProductoRow()]);
    setIsFormPopupVisible(true);
  };

  const handleEdit = (row: ManifoldGridRow) => {
    if (soloLectura) return;
    const registro = registros.find((r) => r.id_Registro === row.id_Registro);
    const productos =
      registro?.productos?.length
        ? registro.productos.map((p) => ({
            id: newProductoRowId(),
            codigo: p.codigo_Producto,
            descripcion: p.producto?.descripcion || "",
          }))
        : [emptyProductoRow()];
    setEditingItem({
      id_Registro: row.id_Registro,
      nombre: row.nombre,
      id_Sucursal: row.id_Sucursal ? Number(row.id_Sucursal) : null,
      capacidad:
        registro?.capacidad && registro.capacidad > 0
          ? registro.capacidad
          : typeof row.capacidad === "number"
            ? row.capacidad
            : null,
    });
    setProductoRows(productos.length > 0 ? productos : [emptyProductoRow()]);
    setIsFormPopupVisible(true);
  };

  const handleToggleStatusRequest = (row: ManifoldGridRow) => {
    if (soloLectura) return;
    setItemToToggle(row);
    setIsStatusPopupVisible(true);
  };

  const confirmToggleStatus = async () => {
    if (!itemToToggle) return;
    try {
      await apiService.patch(
        `/api/Catalogos/Registro_Manifold/${itemToToggle.id_Registro}/cambiastatus`,
        "",
      );
      notify(
        itemToToggle.activo
          ? "Manifold desactivado con éxito."
          : "Manifold activado con éxito.",
        "success",
        2000,
      );
      await fetchData();
    } catch (err) {
      notify(handleApiError(err).message, "error", 3000);
    } finally {
      setIsStatusPopupVisible(false);
      setItemToToggle(null);
    }
  };

  const handleSave = async () => {
    if (soloLectura) return;
    const form = formRef.current;
    if (!form) return;

    const validation = await form.validate();
    if (!validation.isValid) {
      notify("Por favor, complete todos los campos requeridos.", "warning", 2000);
      return;
    }

    const formData = form.option("formData") as FormState;
    if (!formData.id_Sucursal) {
      notify("La sucursal es obligatoria. El manifold se relaciona con esa sucursal.", "warning", 2500);
      return;
    }
    if (!formData.capacidad || Number(formData.capacidad) <= 0) {
      notify("El total de cilindros es obligatorio y debe ser mayor a 0.", "warning", 2500);
      return;
    }

    const codigos = productoRows
      .map((row) => Number(row.codigo))
      .filter((codigo) => codigo > 0);
    if (codigos.length === 0) {
      notify("Debe asociar al menos un producto al manifold.", "warning", 2500);
      return;
    }
    if (productoRows.some((row) => !row.codigo || row.codigo <= 0)) {
      notify("Complete todos los códigos de producto o quite las filas vacías.", "warning", 2500);
      return;
    }
    if (productoRows.some((row) => row.descripcion === "Producto no encontrado")) {
      notify("Hay códigos de producto que no existen.", "warning", 2500);
      return;
    }
    const unique = [...new Set(codigos)];
    if (unique.length !== codigos.length) {
      notify("No se puede asociar el mismo producto más de una vez.", "warning", 2500);
      return;
    }

    const payload: RegistroManifoldDto = {
      nombre: formData.nombre.trim(),
      id_Sucursal: Number(formData.id_Sucursal),
      codigo_Producto: unique[0],
      codigos_Producto: unique,
      capacidad: Number(formData.capacidad),
    };

    try {
      if (formData.id_Registro) {
        await apiService.put<RegistroManifold, RegistroManifoldDto>(
          `/api/Catalogos/Registro_Manifold/${formData.id_Registro}`,
          payload,
        );
        notify("Manifold actualizado con éxito.", "success", 2000);
      } else {
        await apiService.post<RegistroManifold, RegistroManifoldDto>(
          "/api/Catalogos/Registro_Manifold",
          payload,
        );
        notify("Manifold creado con éxito.", "success", 2000);
      }
      setIsFormPopupVisible(false);
      await fetchData();
    } catch (err) {
      notify(handleApiError(err).message, "error", 3000);
    }
  };

  const NombreCell = (cellData: { data: ManifoldGridRow }) => (
    <S.NameLink type="button" onClick={() => handleEdit(cellData.data)}>
      {cellData.data.nombre}
    </S.NameLink>
  );

  const EstatusCell = (cellData: { data: ManifoldGridRow }) => (
    <S.EstatusBadge $active={cellData.data.activo}>
      {cellData.data.activo ? "Activo" : "Inactivo"}
    </S.EstatusBadge>
  );

  const ActionsCell = (cellData: { data: ManifoldGridRow }) => {
    const row = cellData.data;
    return (
      <div
        style={{
          display: "flex",
          gap: 10,
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Button
          hint="Editar"
          onClick={() => handleEdit(row)}
          stylingMode="text"
        >
          <FiEdit size={18} color="#1d4ed8" />
        </Button>
        <Button
          hint={row.activo ? "Desactivar" : "Activar"}
          onClick={() => handleToggleStatusRequest(row)}
          stylingMode="text"
        >
          {row.activo ? (
            <FiTrash2 size={18} color="#dc2626" />
          ) : (
            <FiCheckCircle size={18} color="#16a34a" />
          )}
        </Button>
      </div>
    );
  };

  return (
    <>
      <S.GlobalPopupStyles />
      <GridContainer title="Configuración Manifold">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
            padding: "16px 24px 12px",
            borderBottom: "1px solid #DFE1E6",
          }}
        >
          <Button
            icon="chevronleft"
            text="Regresar"
            stylingMode="outlined"
            onClick={() => navigate(-1)}
          />
        </div>

        <DataGrid
          ref={dataGridRef}
          dataSource={dataSource}
          keyExpr="id_Registro"
          showBorders
          columnAutoWidth
          wordWrapEnabled
          hoverStateEnabled
          loadPanel={{ enabled: loading }}
        >
          <Paging defaultPageSize={10} />
          <FilterRow visible />
          <HeaderFilter visible allowSearch />
          <SearchPanel visible highlightCaseSensitive placeholder="Buscar..." />
          <Toolbar>
            <Item name="searchPanel" />
            <Item
              location="after"
              widget="dxButton"
              options={{
                text: "Exportar Excel",
                icon: "xlsxfile",
                stylingMode: "contained",
                type: "default",
                onClick: handleExportExcel,
              }}
            />
            {!soloLectura && (
            <Item
              location="after"
              widget="dxButton"
              options={{
                text: "Añadir Manifold",
                icon: "add",
                stylingMode: "contained",
                type: "default",
                onClick: handleAdd,
              }}
            />
            )}
          </Toolbar>

          <Column
            dataField="nombre"
            caption="Nombre Manifold"
            cellRender={soloLectura ? undefined : NombreCell}
            minWidth={160}
          />
          <Column
            dataField="numCodSucursal"
            caption="Núm. Cod. Sucursal"
            width={140}
            alignment="center"
          />
          <Column
            dataField="nombreSucursal"
            caption="Nombre Sucursal"
            minWidth={140}
          />
          <Column
            dataField="capacidad"
            caption="Total de cilindros"
            width={170}
            alignment="center"
          />
          <Column
            dataField="codigoProducto"
            caption="Núm. Cod. Producto"
            minWidth={160}
            alignment="center"
          />
          <Column
            dataField="detalleProducto"
            caption="Detalle producto"
            minWidth={260}
          />
          <Column
            dataField="estatus"
            caption="Estatus"
            width={110}
            alignment="center"
            cellRender={EstatusCell}
          />
          {!soloLectura && (
          <Column
            caption="Acciones"
            width={130}
            alignment="center"
            cellRender={ActionsCell}
            allowExporting={false}
          />
          )}
        </DataGrid>
      </GridContainer>

      <Popup
        visible={isFormPopupVisible}
        onHiding={() => setIsFormPopupVisible(false)}
        dragEnabled={false}
        showCloseButton
        showTitle
        title={
          editingItem?.id_Registro
            ? `Editar Manifold: ${editingItem.nombre}`
            : "Añadir Manifold"
        }
        width={620}
        maxHeight="85vh"
        height="auto"
        className="custom-form-popup"
      >
        <S.FormContainer>
          <Form
            formData={editingItem}
            onInitialized={(e) => {
              formRef.current = e.component ?? null;
            }}
          >
            <SimpleItem dataField="nombre">
              <Label text="Nombre Manifold" />
              <RequiredRule message="El nombre es requerido." />
            </SimpleItem>
            <SimpleItem
              dataField="id_Sucursal"
              editorType="dxSelectBox"
              editorOptions={{
                items: sucursales,
                displayExpr: (item: SucursalOption) =>
                  item
                    ? `${item.numSucursal ?? ""} — ${item.nombreSuc ?? ""}`
                    : "",
                valueExpr: "idNum",
                searchEnabled: true,
                placeholder: "Seleccione sucursal",
              }}
            >
              <Label text="Sucursal" />
              <RequiredRule message="La sucursal es requerida." />
            </SimpleItem>
            <SimpleItem
              dataField="capacidad"
              editorType="dxNumberBox"
              editorOptions={{
                min: 1,
                showSpinButtons: false,
                placeholder: "Máximo de envases",
              }}
            >
              <Label text="Total de cilindros" />
              <RequiredRule message="El total de cilindros es requerido." />
              <RangeRule min={1} message="Debe ser al menos 1." />
            </SimpleItem>
          </Form>
          <S.CapacityWarning>
            Esta capacidad indica el número de posiciones activas que tiene el
            Manifold que se está configurando. En la app móvil se mostrará como
            el máximo de envases que se pueden llenar al mismo tiempo en este
            Manifold.
          </S.CapacityWarning>
          <S.ProductSection>
            <S.ProductSectionHeader>
              <S.ProductSectionTitle>Productos asociados</S.ProductSectionTitle>
              <Button
                text="Agregar producto"
                icon="plus"
                stylingMode="outlined"
                type="default"
                onClick={handleAddProducto}
              />
            </S.ProductSectionHeader>
            {productoRows.map((row) => (
              <S.ProductRow key={row.id}>
                <S.ProductRowFields>
                  <NumberBox
                    value={row.codigo ?? undefined}
                    min={1}
                    showSpinButtons={false}
                    placeholder="Código de producto"
                    valueChangeEvent="keyup"
                    onValueChanged={(e) =>
                      handleProductoCodigoChange(
                        row.id,
                        e.value == null ? null : Number(e.value) || null,
                      )
                    }
                  />
                  <S.ProductRowHint>
                    {row.descripcion
                      ? `Detalle producto: ${row.descripcion}`
                      : "Núm. Cod. Producto"}
                  </S.ProductRowHint>
                </S.ProductRowFields>
                <Button
                  icon="trash"
                  hint="Quitar producto"
                  stylingMode="text"
                  type="danger"
                  disabled={productoRows.length <= 1}
                  onClick={() => handleRemoveProducto(row.id)}
                />
              </S.ProductRow>
            ))}
          </S.ProductSection>
        </S.FormContainer>
        <S.PopupFooter>
          <S.CancelButton onClick={() => setIsFormPopupVisible(false)}>
            Cancelar
          </S.CancelButton>
          <S.SaveButton onClick={handleSave}>Guardar</S.SaveButton>
        </S.PopupFooter>
      </Popup>

      <Popup
        visible={isStatusPopupVisible}
        onHiding={() => {
          setIsStatusPopupVisible(false);
          setItemToToggle(null);
        }}
        dragEnabled={false}
        showTitle
        title={`Confirmar ${itemToToggle?.activo ? "desactivación" : "activación"}`}
        width={420}
        height="auto"
        toolbarItems={[
          {
            toolbar: "bottom",
            location: "after",
            widget: "dxButton",
            options: {
              text: `Sí, ${itemToToggle?.activo ? "desactivar" : "activar"}`,
              type: itemToToggle?.activo ? "danger" : "success",
              stylingMode: "contained",
              onClick: confirmToggleStatus,
            },
          },
          {
            toolbar: "bottom",
            location: "after",
            widget: "dxButton",
            options: {
              text: "Cancelar",
              stylingMode: "text",
              onClick: () => {
                setIsStatusPopupVisible(false);
                setItemToToggle(null);
              },
            },
          },
        ]}
      >
        <div style={{ padding: 20, textAlign: "center" }}>
          <p>
            ¿Estás seguro de que deseas{" "}
            <strong>
              {itemToToggle?.activo ? "desactivar" : "activar"}
            </strong>{" "}
            el manifold
          </p>
          <p style={{ fontWeight: "bold", margin: "10px 0" }}>
            "{itemToToggle?.nombre}"?
          </p>
        </div>
      </Popup>
    </>
  );
};

export default ManifoldIndex;
